import AttorneyHubAPIService from "_shared/_api/attorney-hub-api-service";
import Guid from "_shared/guids/guid";
import axios, { AxiosHeaders, CanceledError, HttpStatusCode } from "axios";
import { InvalidRegistrationInfoError } from "legal-entities/registration-info/registration-info";
import AdministratorInvitation, {
  AdministratorInvitationNotFoundError,
  UnauthorizedAdministratorInvitationAccessError,
} from "legal-entities/invitations/administrator-invitations/administrator-invitation";
import AcceptEntityUserInvitationAPIRequest from "legal-entities/invitations/member-invitations/email-based/_api/commands/accept-entity-user-invitation-api-command";
import LegalEntity, { LegalEntityNotFoundError } from "legal-entities/legal-entity";
import Session from "users/__session/session";
import { AuthInfo } from "users/auth/info/auth-info";
import AuthInfoSchema from "users/auth/info/auth-info-schema";
import AdministratorInvitationSchema from "legal-entities/invitations/administrator-invitations/_schema/administrator-invitation-schema";

export default class AdministratorInvitationAPIService {
  private readonly headers: AxiosHeaders = new AxiosHeaders();

  public constructor(session: Readonly<Session>) {
    // Some calls in this service will be made by users without valid session auth tokens
    if (session.authToken?.value) {
      this.headers.set("Authorization", `Bearer ${session.authToken.value}`);
    }
  }

  public async createEntityAdminInvitation(invitation: AdministratorInvitation): Promise<void> {
    const url = new URL(
      `/legal-entities/${invitation.entity.id.value}/admin-invitations`,
      AttorneyHubAPIService.apiBaseUrl,
    );
    try {
      await axios.post(url.toString(), invitation, {
        headers: this.headers.concat({ "Content-Type": "application/json-patch+json" }),
      });
    } catch (error) {
      if (error instanceof CanceledError) throw error;
      throw new AdministratorInvitationAPIServiceError("createEntityAdminInvitation", error, invitation);
    }
  }

  public async getAdministratorInvitationById(
    id: Guid,
    abortController?: AbortController,
  ): Promise<AdministratorInvitation> {
    const url = new URL(`/legal-entities/admin-invitations/${id.value}`, AttorneyHubAPIService.apiBaseUrl);
    try {
      const response = await axios.get(url.toString(), {
        headers: this.headers,
        signal: abortController?.signal,
      });
      const parsed = AdministratorInvitationSchema.deserialize(response.data);
      return new AdministratorInvitation(parsed);
    } catch (error) {
      if (error instanceof CanceledError) throw error;
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        throw new AdministratorInvitationNotFoundError(id);
      }
      throw new AdministratorInvitationAPIServiceError("getAdministratorInvitationById", error);
    }
  }

  public async getAdminInvitationsForLegalEntity(
    entity: LegalEntity,
    abortController?: AbortController,
  ): Promise<AdministratorInvitation[]> {
    const url = new URL(
      `/legal-entities/admin-invitations?legalEntityId=${entity.id.value}`,
      AttorneyHubAPIService.apiBaseUrl,
    );
    try {
      const response = await axios.get(url.toString(), {
        headers: this.headers,
        signal: abortController?.signal,
      });
      return response.data.map((invitationData: unknown): AdministratorInvitation => {
        const parsed = AdministratorInvitationSchema.deserialize(invitationData);
        return new AdministratorInvitation(parsed);
      });
    } catch (error) {
      if (error instanceof CanceledError) throw error;
      if (axios.isAxiosError(error) && error.response?.status === HttpStatusCode.Forbidden) {
        throw new UnauthorizedAdministratorInvitationAccessError(entity.id);
      }
      if (axios.isAxiosError(error) && error.response?.status === HttpStatusCode.NotFound) {
        throw new LegalEntityNotFoundError(entity.id);
      }
      throw new AdministratorInvitationAPIServiceError("getAdminInvitationsForLegalEntity", error);
    }
  }

  public async acceptAdministratorInvitation(
    invitation: AdministratorInvitation,
    session: Session,
    isClient?: boolean,
    isVendor?: boolean,
  ): Promise<void> {
    if (!invitation.id) throw new Error("Invitation ID is required to accept an invitation.");
    if (!invitation.registrationInfo?.password) throw new Error("Password is required to accept an invitation.");
    const url = new URL(
      `/legal-entities/admin-invitations/${invitation.id.value}/accept`,
      AttorneyHubAPIService.apiBaseUrl,
    );
    try {
      const apiResponse = await axios.post(
        url.toString(),
        new AcceptEntityUserInvitationAPIRequest(
          invitation.registrationInfo.password,
          invitation.registrationInfo.name,
          isClient,
          isVendor,
        ),
        {
          headers: this.headers.concat({ "Content-Type": "application/json-patch+json" }),
        },
      );
      const authResponse = new AuthInfo(AuthInfoSchema.deserialize(apiResponse.data)).toAuthResponse();
      session.initializeFromAuthResponse(authResponse);
    } catch (error) {
      if (error instanceof CanceledError) throw error;
      if (axios.isAxiosError(error) && error.response?.status === HttpStatusCode.BadRequest) {
        throw new InvalidRegistrationInfoError(error.response.data);
      }
      if (axios.isAxiosError(error) && error.response?.status === HttpStatusCode.NotFound) {
        throw new AdministratorInvitationNotFoundError(invitation.id);
      }
      throw new AdministratorInvitationAPIServiceError("acceptAdministratorInvitation", error, invitation);
    }
  }

  public async cancelAdministratorInvitation(invitation: AdministratorInvitation): Promise<void> {
    if (!invitation.id) throw new Error("Invitation ID is required to cancel an invitation.");
    const url = new URL(
      `/legal-entities/admin-invitations/${invitation.id.value}/cancel`,
      AttorneyHubAPIService.apiBaseUrl,
    );
    try {
      await axios.post(
        url.toString(),
        {},
        {
          headers: this.headers,
        },
      );
    } catch (error) {
      if (axios.isAxiosError(error) && error?.response?.status === HttpStatusCode.NotFound) {
        throw new AdministratorInvitationNotFoundError(invitation.id);
      }
      throw new AdministratorInvitationAPIServiceError("cancelAdministratorInvitation", error, invitation);
    }
  }
}

export class AdministratorInvitationAPIServiceError extends Error {
  public methodName: string;
  public error: unknown;
  public invitation?: AdministratorInvitation;
  public constructor(methodName: string, error: unknown, invitation?: AdministratorInvitation) {
    super(
      `Error in AdministratorInvitationAPIService.${methodName}: ${
        error instanceof Error ? error.message : String(error)
      }`,
    );
    this.methodName = methodName;
    this.error = error;
    this.invitation = invitation;
  }
}
